import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text } from '../../common/Typography';
import { Button, EmptyState, LoadingState, SectionHeader, StatusBadge } from '../../common';
import { Card } from '../../common/Card';
import { fetchSessionReviewGames } from '../../../services/sessionReviewService';
import { getSessionPartnerships } from '../../../services/sessionPartnershipService';
import { buildSessionHealth } from '../../../utils/session/sessionHealth';

const formatPct = (value) => (value == null ? '—' : `${Math.round(value * 100)}%`);

const formatAvg = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const verdictTone = (verdict = '') => {
  if (verdict.startsWith('Competitive games')) return 'success';
  if (verdict.startsWith('Playing time')) return 'danger';
  if (verdict.startsWith('No scored')) return 'default';
  return 'warning';
};

const Kpi = ({ label, value, hint }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums leading-none text-text-primary">{value}</p>
    {hint ? (
      <p className="mt-1 text-xs leading-snug text-text-secondary">{hint}</p>
    ) : null}
  </div>
);

const StackedBar = ({ segments, ariaLabel }) => {
  const total = segments.reduce((sum, segment) => sum + (segment.value || 0), 0);
  return (
    <div
      className="flex h-2.5 overflow-hidden rounded-full bg-surface-subtle"
      role="img"
      aria-label={ariaLabel}
    >
      {segments.map((segment) => {
        if (!segment.value || total === 0) return null;
        return (
          <div
            key={segment.key}
            className={`h-full ${segment.barClass}`}
            style={{ width: `${(segment.value / total) * 100}%` }}
          />
        );
      })}
    </div>
  );
};

const ShareBar = ({ value, barClass = 'bg-primary', ariaLabel }) => {
  const pct = Math.max(0, Math.min(100, Math.round((value || 0) * 100)));
  return (
    <div
      className="h-2 overflow-hidden rounded-full bg-surface-subtle"
      role="img"
      aria-label={ariaLabel}
    >
      <div className={`h-full rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const LegendDot = ({ className, label, value }) => (
  <div className="flex min-w-0 items-center gap-2 text-xs text-text-secondary">
    <span className={`h-2 w-2 shrink-0 rounded-full ${className}`} aria-hidden />
    <span className="truncate">{label}</span>
    <span className="ml-auto tabular-nums font-medium text-text-primary">{value}</span>
  </div>
);

const DashboardCard = ({ title, description, infoText, action, children }) => (
  <Card variant="default" size="small" className="!p-4 sm:!p-5">
    <SectionHeader
      title={title}
      description={description}
      infoText={infoText}
      size="sm"
      variant="primary"
      action={action}
      className="!mb-4"
    />
    {children}
  </Card>
);

const SessionAnalysisPanel = ({
  sessionId,
  sessionPlayers = [],
  playersLoading = false,
  onNavigateTab,
  pointsTarget = 21,
}) => {
  const [games, setGames] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAnalysis = useCallback(async () => {
    if (!sessionId) {
      setGames([]);
      setPartnerships([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [data, partnershipRes] = await Promise.all([
        fetchSessionReviewGames(sessionId),
        getSessionPartnerships(sessionId),
      ]);
      setGames(Array.isArray(data) ? data : []);
      setPartnerships(partnershipRes?.success && Array.isArray(partnershipRes.data)
        ? partnershipRes.data
        : []);
    } catch (err) {
      console.error('Error loading session analysis games:', err);
      setError('Failed to load session analysis');
      setGames([]);
      setPartnerships([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  const health = useMemo(
    () => buildSessionHealth(games, sessionPlayers, partnerships, { pointsTarget }),
    [games, sessionPlayers, partnerships, pointsTarget]
  );

  const waitingForRoster = playersLoading && sessionPlayers.length === 0;

  if (loading || waitingForRoster) {
    return <LoadingState message="Loading session analysis…" />;
  }

  if (error) {
    return (
      <EmptyState
        title="Could not load analysis"
        description={error}
        action={(
          <Button type="button" variant="outline" size="small" onClick={loadAnalysis}>
            Try again
          </Button>
        )}
      />
    );
  }

  if (health.totalGames === 0) {
    return (
      <EmptyState
        title="No games to analyse"
        description="Once games are logged, this tab will show how close, fresh, and even the night was."
      />
    );
  }

  return (
    <SessionAnalysisDashboard health={health} onNavigateTab={onNavigateTab} />
  );
};

const SessionAnalysisDashboard = ({ health, onNavigateTab }) => {
  const { closeness, closenessThresholds, novelty, paperBalance, playingTime, attendance, gameTypes, social } = health;
  const competitiveMax = closenessThresholds?.competitiveMax ?? 5;
  const pointsTarget = closenessThresholds?.pointsTarget ?? 21;
  const goTo = (tab) => {
    if (onNavigateTab) onNavigateTab(tab);
  };

  const closenessSegments = [
    { key: 'close', label: 'Close', value: closeness.close, barClass: 'bg-success' },
    { key: 'competitive', label: 'Competitive', value: closeness.competitive, barClass: 'bg-warning' },
    { key: 'oneSided', label: 'One-sided', value: closeness.oneSided, barClass: 'bg-danger' },
  ];

  const genderSegments = [
    { key: 'male', label: 'Men', value: attendance.male, barClass: 'bg-male' },
    { key: 'female', label: 'Women', value: attendance.female, barClass: 'bg-female' },
    { key: 'unspecified', label: 'Unspecified', value: attendance.unspecified, barClass: 'bg-text-muted' },
  ];

  const gameTypeSegments = [
    { key: 'mens', label: "Men's", value: gameTypes.mens, barClass: 'bg-male' },
    { key: 'womens', label: "Women's", value: gameTypes.womens, barClass: 'bg-female' },
    { key: 'mixed', label: 'Mixed', value: gameTypes.mixed, barClass: 'bg-primary' },
    { key: 'other', label: 'Other', value: gameTypes.other, barClass: 'bg-text-muted' },
    { key: 'unbalanced', label: 'Unbalanced', value: gameTypes.unbalanced, barClass: 'bg-danger' },
  ];

  const playingRange = playingTime.min != null
    ? `${playingTime.min}–${playingTime.max}`
    : '—';

  return (
    <div className="space-y-4 sm:space-y-5">
      <Card variant="default" size="small" className="!p-4 sm:!p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Text className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Session verdict
          </Text>
          <StatusBadge variant={verdictTone(health.verdict)} size="small">
            {health.scoredGames} scored of {health.totalGames}
          </StatusBadge>
        </div>
        <p className="mt-2 text-xl font-semibold leading-snug text-text-primary sm:text-2xl">
          {health.verdict}
        </p>
        <div className="mt-5 grid grid-cols-3 gap-x-6 gap-y-4 border-t border-border-medium pt-4">
          <Kpi
            label="Competitive"
            value={formatPct(health.competitiveShare)}
            hint={`To ${pointsTarget}, margin of ${competitiveMax} or less`}
          />
          <Kpi
            label="Novelty"
            value={formatPct(novelty.overall)}
            hint="New people on court"
          />
          <Kpi
            label="Games each"
            value={playingRange}
            hint={`Avg ${formatAvg(playingTime.avg)}`}
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DashboardCard
          title="Scoreboard"
          description="How close the games actually were."
          infoText={`Competitive includes close games: any scored game to ${pointsTarget} with a margin of ${competitiveMax} or less.`}
          action={(
            <Button type="button" variant="link" size="small" onClick={() => goTo('games')}>
              Games
            </Button>
          )}
        >
          <StackedBar
            segments={closenessSegments}
            ariaLabel={`${closeness.close} close, ${closeness.competitive} competitive, ${closeness.oneSided} one-sided`}
          />
          <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-3 sm:gap-3">
            {closenessSegments.map((segment) => (
              <LegendDot
                key={segment.key}
                className={segment.barClass}
                label={segment.label}
                value={segment.value}
              />
            ))}
          </div>

          <div className="mt-5 space-y-3">
            <div>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <Text className="text-xs font-medium text-text-secondary">Novelty first half</Text>
                <span className="text-sm font-semibold tabular-nums text-text-primary">
                  {formatPct(novelty.firstHalf)}
                </span>
              </div>
              <ShareBar
                value={novelty.firstHalf}
                barClass="bg-primary"
                ariaLabel={`First-half novelty ${formatPct(novelty.firstHalf)}`}
              />
            </div>
            <div>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <Text className="text-xs font-medium text-text-secondary">Novelty second half</Text>
                <span className="text-sm font-semibold tabular-nums text-text-primary">
                  {formatPct(novelty.secondHalf)}
                </span>
              </div>
              <ShareBar
                value={novelty.secondHalf}
                barClass="bg-primary/70"
                ariaLabel={`Second-half novelty ${formatPct(novelty.secondHalf)}`}
              />
            </div>
          </div>

          {(health.quartetRepeats > 0 || health.alteredGames > 0) ? (
            <Text className="mt-4 text-sm text-text-secondary">
              {health.quartetRepeats > 0 ? `${health.quartetRepeats} quartet repeats` : ''}
              {health.quartetRepeats > 0 && health.alteredGames > 0 ? ' · ' : ''}
              {health.alteredGames > 0 ? `${health.alteredGames} host-altered` : ''}
            </Text>
          ) : null}
        </DashboardCard>

        <DashboardCard
          title="The room"
          description="Who came, and whether playing time stayed even."
        >
          <StackedBar
            segments={genderSegments}
            ariaLabel={`${attendance.male} men, ${attendance.female} women`}
          />
          <div className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-3 sm:gap-3">
            {genderSegments.filter((segment) => segment.value > 0 || segment.key !== 'unspecified').map((segment) => (
              <LegendDot
                key={segment.key}
                className={segment.barClass}
                label={segment.label}
                value={segment.value}
              />
            ))}
            <LegendDot className="bg-text-muted" label="No-shows" value={attendance.noShows} />
          </div>

          <div className="mt-5">
            <Text className="text-xs font-medium text-text-secondary">Playing time</Text>
            <p className="mt-1 text-sm text-text-primary">
              {playingTime.offMedian.length > 0
                ? `${playingTime.offMedian.length} player${playingTime.offMedian.length === 1 ? '' : 's'} more than one game from the median.`
                : 'Everyone who played stayed within one game of the median.'}
            </p>
            <Text className="mt-2 text-xs text-text-muted">
              Men avg {formatAvg(playingTime.avgByGender.male)} · women avg {formatAvg(playingTime.avgByGender.female)}
            </Text>
          </div>

          <div className="mt-5">
            <Text className="text-xs font-medium text-text-secondary">Game types</Text>
            <div className="mt-2">
              <StackedBar
                segments={gameTypeSegments}
                ariaLabel="Game type mix"
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-1.5">
              {gameTypeSegments.filter((segment) => segment.value > 0).map((segment) => (
                <LegendDot
                  key={segment.key}
                  className={segment.barClass}
                  label={segment.label}
                  value={segment.value}
                />
              ))}
            </div>
          </div>
        </DashboardCard>

        {paperBalance.hasEloData ? (
          <DashboardCard
            title="On paper"
            description="Whether matchmaking set up even games — not the scoreline."
            action={(
              <Button type="button" variant="link" size="small" onClick={() => goTo('session-ranking')}>
                Rankings
              </Button>
            )}
          >
            <div className="space-y-3">
              <div>
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <Text className="text-xs font-medium text-text-secondary">Team Elo within 50</Text>
                  <span className="text-sm font-semibold tabular-nums text-text-primary">
                    {formatPct(paperBalance.ratedGames ? paperBalance.within50 / paperBalance.ratedGames : null)}
                  </span>
                </div>
                <ShareBar
                  value={paperBalance.ratedGames ? paperBalance.within50 / paperBalance.ratedGames : 0}
                  barClass="bg-success"
                  ariaLabel={`${paperBalance.within50} of ${paperBalance.ratedGames} games within 50 Elo`}
                />
              </div>
              <div>
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <Text className="text-xs font-medium text-text-secondary">Team Elo within 100</Text>
                  <span className="text-sm font-semibold tabular-nums text-text-primary">
                    {formatPct(paperBalance.ratedGames ? paperBalance.within100 / paperBalance.ratedGames : null)}
                  </span>
                </div>
                <ShareBar
                  value={paperBalance.ratedGames ? paperBalance.within100 / paperBalance.ratedGames : 0}
                  barClass="bg-success/70"
                  ariaLabel={`${paperBalance.within100} of ${paperBalance.ratedGames} games within 100 Elo`}
                />
              </div>
            </div>
            <Text className="mt-4 text-sm text-text-secondary">
              {health.upsets} upset{health.upsets === 1 ? '' : 's'}
              {health.alteredGames > 0 ? ` · ${health.alteredGames} host-altered` : ''}
            </Text>
          </DashboardCard>
        ) : (
          <DashboardCard title="On paper" description="Starting ratings are not available for these games.">
            <Text className="text-sm text-text-secondary">
              On-paper balance is hidden when games were logged without ratings.
            </Text>
          </DashboardCard>
        )}

        <DashboardCard
          title="Rotation"
          description="Whether people kept meeting new partners."
          infoText="A stuck pair partnered in over half of either player’s games, and was not a formal session partnership."
          action={(
            <Button type="button" variant="link" size="small" onClick={() => goTo('player-interactions')}>
              Interactions
            </Button>
          )}
        >
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <Text className="text-xs font-medium text-text-secondary">Pairs who shared a court</Text>
            <span className="text-sm font-semibold tabular-nums text-text-primary">
              {formatPct(social.coverage)}
            </span>
          </div>
          <ShareBar
            value={social.coverage}
            barClass="bg-primary"
            ariaLabel={`${social.pairsSharedCourt} of ${social.possiblePairs} pairs shared a court`}
          />
          <Text className="mt-2 text-xs text-text-muted">
            {social.pairsSharedCourt} of {social.possiblePairs} possible pairs
          </Text>

          <div className="mt-5">
            <Text className="text-xs font-medium text-text-secondary">Stuck pairs</Text>
            {social.stuckPairs.length === 0 ? (
              <p className="mt-1 text-sm text-text-primary">
                None — nobody was locked to the same partner.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-border-medium">
                {social.stuckPairs.slice(0, 6).map((pair) => (
                  <li
                    key={`${pair.playerA.id}-${pair.playerB.id}`}
                    className="flex items-center justify-between gap-3 py-2 first:pt-0"
                  >
                    <span className="truncate text-sm text-text-primary">
                      {pair.playerA.name} & {pair.playerB.name}
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-text-primary">
                      {formatPct(pair.share)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DashboardCard>
      </div>
    </div>
  );
};

export default SessionAnalysisPanel;
